import { useEffect, useMemo, useState } from "react";
import axiosInstance from "../../api/axiosInstance";

/* ----------------------------- Inline Donut ----------------------------- */
/* Self-contained SVG donut chart — no external chart library required. */
const InlineDonut = ({ data = [], colors = [], size = 140, strokeWidth = 18, loading = false }) => {
  const total = data.reduce((sum, v) => sum + (v || 0), 0);

  if (loading) {
    return <div className="rounded-full bg-gray-200 animate-pulse" style={{ width: size, height: size }} />;
  }

  if (!total) {
    return (
      <div className="rounded-full border-8 border-gray-100 flex items-center justify-center text-[10px] text-gray-400" style={{ width: size, height: size }}>
        No data
      </div>
    );
  }

  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  let cumulative = 0;

  const segments = data.map((value, idx) => {
    const fraction = (value || 0) / total;
    const dash = fraction * circumference;
    const gap = circumference - dash;
    const offset = -cumulative * circumference;
    cumulative += fraction;

    return <circle key={idx} cx={size / 2} cy={size / 2} r={radius} fill="transparent" stroke={colors[idx] || "#ccc"} strokeWidth={strokeWidth} strokeDasharray={`${dash} ${gap}`} strokeDashoffset={offset} strokeLinecap="butt" transform={`rotate(-90 ${size / 2} ${size / 2})`} />;
  });

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      <circle cx={size / 2} cy={size / 2} r={radius} fill="transparent" stroke="#f1f5f9" strokeWidth={strokeWidth} />
      {segments}
      <text x="50%" y="50%" textAnchor="middle" dominantBaseline="middle" className="fill-gray-700" style={{ fontSize: 15, fontWeight: 700 }}>
        {total.toLocaleString()}
      </text>
    </svg>
  );
};

/* ------------------------------- KPI Card -------------------------------- */
const StatCard = ({ label, value, sub, color = "#118ab2", loading }) => (
  <div className="bg-white rounded-xl shadow-md shadow-slate-200 border border-gray-100 !p-4 flex flex-col gap-1">
    <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">{label}</span>
    {loading ? (
      <div className="h-7 w-16 bg-gray-200 rounded animate-pulse !mt-1" />
    ) : (
      <span className="text-2xl font-bold" style={{ color }}>
        {(value ?? 0).toLocaleString()}
      </span>
    )}
    {sub && !loading && <span className="text-xs text-gray-400">{sub}</span>}
  </div>
);

/* ----------------------------- Legend Row -------------------------------- */
const BreakdownRow = ({ label, value, color, total }) => {
  const pct = total > 0 ? Math.round(((value || 0) / total) * 100) : 0;
  return (
    <div className="flex items-center gap-2 text-xs !py-1">
      <span className="inline-block w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ backgroundColor: color }} />
      <span className="flex-1 text-gray-600 truncate" title={label}>
        {label}
      </span>
      <span className="font-semibold text-gray-800 w-10 text-right">{value ?? 0}</span>
      <span className="text-gray-400 w-9 text-right">{pct}%</span>
    </div>
  );
};

/* ------------------------------ Source Card ------------------------------ */
const SourceCard = ({ title, rows, colors, loading }) => {
  const total = rows.reduce((sum, r) => sum + (r.value || 0), 0);
  const chartData = rows.map((r) => r.value || 0);

  return (
    <div className="bg-white rounded-xl shadow-md shadow-slate-200 border border-gray-100 !p-4">
      <div className="flex items-center justify-between !mb-3">
        <h3 className="font-semibold text-gray-700 text-sm">{title}</h3>
        <span className="text-xs text-gray-400">{loading ? "…" : `${total} total`}</span>
      </div>

      <div className="grid grid-cols-12 items-center gap-2">
        <div className="col-span-5 flex justify-center">
          <InlineDonut data={chartData} colors={colors} size={120} strokeWidth={16} loading={loading} />
        </div>
        <div className="col-span-7 flex flex-col">
          {rows.map((r, i) => (
            <BreakdownRow key={i} label={r.label} value={r.value} color={colors[i]} total={total} />
          ))}
        </div>
      </div>
    </div>
  );
};

/* ---------------------------------- Page ---------------------------------- */
const Stats2 = () => {
  const [statsLoading, setStatsLoading] = useState(false);
  const [stats, setStats] = useState(null);
  const [dmStatsLoading, setDmStatsLoading] = useState(false);
  const [dmStats, setDmStats] = useState(null);
  const [timePeriod, setTimePeriod] = useState("today");

  const rowColors = [
    "#06d6a0", // Found
    "#ffd166", // Not Found L1
    "#ef476f", // Not Found L2
    "#118ab2", // Deleted
    "#8338ec", // DM Leads
    "#ff6b6b", // GHL Saved
    "#c77dff", // GHL Saved (Old)
    "#4cc9f0", // GHL Not Saved
  ];

  const fetchStats = async ({ showLoader = true }) => {
    try {
      if (showLoader) setStatsLoading(true);
      const resp = await axiosInstance.get(`api/ispeed_properties/stats?tables=leads_m,leads_b,leads_gs,leads_tpl&timePeriod=${timePeriod}`);
      if (resp.status === 200) {
        setStats(resp.data.data);
      }
    } catch (err) {
      console.error("Failed to fetch leads", err);
    } finally {
      setStatsLoading(false);
    }
  };

  const fetchStatsDMLeads = async ({ showLoader = true }) => {
    try {
      if (showLoader) setDmStatsLoading(true);
      const resp = await axiosInstance.get(`api/ispeed_properties/stats-dm-leads?sources=m,b,g,t,none&timePeriod=${timePeriod}`);
      if (resp.status === 200) {
        setDmStats(resp.data.data);
      }
    } catch (err) {
      console.error("Failed to fetch leads", err);
    } finally {
      setDmStatsLoading(false);
    }
  };

  useEffect(() => {
    fetchStats({ showLoader: true });
    fetchStatsDMLeads({ showLoader: true });

    const interval = setInterval(() => {
      fetchStats({ showLoader: false });
      fetchStatsDMLeads({ showLoader: false });
    }, 60000);

    return () => clearInterval(interval); // eslint-disable-next-line
  }, [timePeriod]);

  const sourceRows = (s) =>
    s
      ? [
          { label: "Found", value: s.found_created },
          { label: "Not Found L-1", value: s.not_found_l1_created },
          { label: "Not Found L-2", value: s.not_found_l2_created },
          { label: "Deleted", value: s.deleted_created },
          { label: "DM Leads", value: s.dm_created },
          { label: "GHL Saved", value: s.contact_saved_same_day },
          { label: "GHL Saved (Old)", value: s.contact_saved_old_ghl_saved },
          { label: "GHL Not Saved", value: s.others_created },
        ]
      : [];

  const totals = useMemo(() => {
    if (!stats) return null;
    const sources = [stats.leads_m, stats.leads_b, stats.leads_gs, stats.leads_tpl];
    const sum = (key) => sources.reduce((acc, s) => acc + (s?.[key] || 0), 0);
    return {
      created: sum("created_leads"),
      found: sum("found_created"),
      notFoundL1: sum("not_found_l1_created"),
      notFoundL2: sum("not_found_l2_created"),
      deleted: sum("deleted_created"),
      dmLeads: sum("dm_created"),
      ghlSavedSameDay: sum("contact_saved_same_day"),
      ghlSavedOld: sum("contact_saved_old_ghl_saved"),
      ghlNotSaved: sum("others_created"),
    };
  }, [stats]);

  const dmTotals = useMemo(() => {
    if (!dmStats) return null;
    const keys = ["m", "b", "g", "t", "none"];
    const sum = (key) => keys.reduce((acc, k) => acc + (dmStats[k]?.[key] || 0), 0);
    return {
      sameDay: sum("contact_saved_same_day"),
      old: sum("contact_saved_old_ghl_saved"),
      notSaved: sum("others_created"),
    };
  }, [dmStats]);

  const dmSourceLabels = { m: "M", b: "B", g: "G", t: "T", none: "Unknown" };

  const Periods = {
    today: "Today",
    yesterday: "Yesterday",
    last7: "Last 7 Days",
    last15: "Last 15 Days",
    last30: "Last 30 Days",
  };

  const dmTotalAll = (dmTotals?.sameDay || 0) + (dmTotals?.old || 0) + (dmTotals?.notSaved || 0);

  return (
    <div className="!px-4 !py-6 !space-y-8">
      {/* Header + period selector */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        {/* <h1 className="text-xl font-bold text-gray-800">Leads Dashboard</h1> */}
        <div></div>
        <select
          className="!p-2.5 rounded-lg border border-gray-300 bg-white text-gray-700 shadow-sm 
               focus:outline-none focus:ring-2 focus:ring-[#ff850f] focus:border-[#ff850f] 
               transition cursor-pointer w-full sm:w-56"
          value={timePeriod}
          onChange={(e) => setTimePeriod(e.target.value)}
        >
          {Object.entries(Periods).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
      </div>

      {/* KPI summary cards */}
      <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        <StatCard label="Total Added" value={totals?.created} loading={statsLoading} color="#06d6a0" />
        <StatCard label="Found" value={totals?.found} loading={statsLoading} color="#06d6a0" />
        <StatCard label="Not Found" value={(totals?.notFoundL1 || 0) + (totals?.notFoundL2 || 0)} sub={`L1: ${totals?.notFoundL1 || 0} · L2: ${totals?.notFoundL2 || 0}`} loading={statsLoading} color="#ffd166" />
        <StatCard label="Deleted" value={totals?.deleted} loading={statsLoading} color="#ef476f" />
        <StatCard label="DM Leads" value={totals?.dmLeads} loading={statsLoading} color="#8338ec" />
        <StatCard label="GHL Saved" value={(totals?.ghlSavedSameDay || 0) + (totals?.ghlSavedOld || 0)} sub={`Same day: ${totals?.ghlSavedSameDay || 0} · Old: ${totals?.ghlSavedOld || 0}`} loading={statsLoading} color="#ff6b6b" />
      </div>

      {/* Per-source breakdown cards */}
      <div>
        <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide !mb-3">Breakdown by Source</h2>
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-2">
          <SourceCard title="Leads M" rows={sourceRows(stats?.leads_m)} colors={rowColors} loading={statsLoading} />
          <SourceCard title="Leads B" rows={sourceRows(stats?.leads_b)} colors={rowColors} loading={statsLoading} />
          <SourceCard title="Leads G" rows={sourceRows(stats?.leads_gs)} colors={rowColors} loading={statsLoading} />
          <SourceCard title="Leads T" rows={sourceRows(stats?.leads_tpl)} colors={rowColors} loading={statsLoading} />
        </div>
      </div>

      {/* DM Leads GHL save breakdown */}
      <div>
        <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide !mb-3">DM Leads — GHL Save Status</h2>
        <div className="grid gap-4 md:grid-cols-12">
          <div className="bg-white rounded-xl shadow-md shadow-slate-200 border border-gray-100 !p-4 md:col-span-4">
            <div className="grid grid-cols-12 items-center gap-2">
              <div className="col-span-5 flex justify-center">
                <InlineDonut data={[dmTotals?.sameDay || 0, dmTotals?.old || 0, dmTotals?.notSaved || 0]} colors={["#ff6b6b", "#c77dff", "#4cc9f0"]} size={140} strokeWidth={18} loading={dmStatsLoading} />
              </div>
              <div className="col-span-7 flex flex-col">
                <BreakdownRow label="GHL Saved (Same Day)" value={dmTotals?.sameDay} color="#ff6b6b" total={dmTotalAll} />
                <BreakdownRow label="GHL Saved (Old)" value={dmTotals?.old} color="#c77dff" total={dmTotalAll} />
                <BreakdownRow label="GHL Not Saved" value={dmTotals?.notSaved} color="#4cc9f0" total={dmTotalAll} />
              </div>
            </div>
          </div>

          {/* Per-source DM table */}
          <div className="bg-white rounded-xl shadow-md shadow-slate-200 border border-gray-100 !p-4 overflow-x-auto md:col-span-8">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-gray-500 border-b border-gray-200">
                  <th className="text-left !py-2 font-medium">Source</th>
                  <th className="text-right !py-2 font-medium">Same Day</th>
                  <th className="text-right !py-2 font-medium">Old</th>
                  <th className="text-right !py-2 font-medium">Not Saved</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(dmSourceLabels).map(([key, label]) => (
                  <tr key={key} className="border-b border-gray-100 last:border-0">
                    <td className="!py-2 font-medium text-gray-700">{label}</td>
                    <td className="!py-2 text-right text-[#ff6b6b] font-semibold">{dmStats?.[key]?.contact_saved_same_day ?? 0}</td>
                    <td className="!py-2 text-right text-[#c77dff] font-semibold">{dmStats?.[key]?.contact_saved_old_ghl_saved ?? 0}</td>
                    <td className="!py-2 text-right text-[#4cc9f0] font-semibold">{dmStats?.[key]?.others_created ?? 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Stats2;
